// Self-contained admin auth UI. Mirrors NPI's entrypoint + lib/tenant/forms
// (login + password reset), trimmed to plain Tailwind + @contember/react-identity
// hooks (no IdP / magic link / toast / design-system dependency).

import { Link } from '@buzola/router'
import {
	LoginForm,
	PasswordResetForm,
	PasswordResetRequestForm,
	useIdentity,
	useLoginForm,
	usePasswordResetForm,
	usePasswordResetRequestForm,
} from '@contember/react-identity'
import { type ReactNode, useEffect } from 'react'
import { adminConfig } from './config'

function Loader() {
	return (
		<div className="flex items-center justify-center py-6">
			<div className="h-8 w-8 animate-spin rounded-full border-2 border-gray-300 border-t-gray-700" />
		</div>
	)
}

function Shell({ title, subtitle, children }: { title?: string; subtitle?: string; children: ReactNode }) {
	return (
		<div className="flex min-h-screen items-center justify-center bg-gray-50 p-6">
			<div className="w-full max-w-sm rounded-lg border border-gray-200 bg-white p-8 shadow-sm">
				<h1 className="mb-1 text-xl font-semibold text-gray-900">{title ?? 'ECCEDU admin'}</h1>
				<p className="mb-6 text-sm text-gray-500">{subtitle ?? 'Sign in to your account'}</p>
				{children}
			</div>
		</div>
	)
}

type AnyForm = {
	state: string
	errors: Array<{ field?: string; code: string; developerMessage?: string }>
	values: Record<string, unknown>
	setValue: (field: string, value: string) => void
}

function FormErrors({ form }: { form: AnyForm }) {
	return (
		<>
			{form.errors
				.filter(e => e.field === undefined)
				.map(e => (
					<p key={e.code} className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
						{e.code === 'INVALID_CREDENTIALS'
							? 'Wrong email or password.'
							: e.code === 'TOKEN_NOT_FOUND' || e.code === 'TOKEN_INVALID'
							? 'Invalid or expired code.'
							: (e.developerMessage ?? e.code)}
					</p>
				))}
		</>
	)
}

function Field({
	form,
	field,
	label,
	type,
	autoComplete,
	autoFocus,
}: {
	form: AnyForm
	field: string
	label: string
	type: string
	autoComplete?: string
	autoFocus?: boolean
}) {
	const invalid = form.errors.some(e => e.field === field)
	return (
		<label className="flex flex-col gap-1.5">
			<span className="text-sm font-medium text-gray-700">{label}</span>
			<input
				className={`rounded-md border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-orange/30 ${invalid ? 'border-red-400' : 'border-gray-300'}`}
				type={type}
				autoComplete={autoComplete}
				autoFocus={autoFocus}
				required
				value={(form.values[field] as string | undefined) ?? ''}
				onChange={e => form.setValue(field, e.target.value)}
			/>
		</label>
	)
}

function SubmitButton({ form, children }: { form: AnyForm; children: ReactNode }) {
	const busy = form.state === 'submitting' || form.state === 'success'
	return (
		<button
			type="submit"
			disabled={busy}
			className="mt-2 rounded-md bg-orange px-4 py-2 text-sm font-medium text-black hover:bg-orange/90 disabled:opacity-60"
		>
			{children}
		</button>
	)
}

function BackToLogin() {
	return (
		<Link to="login">
			<a className="mt-4 block text-center text-sm text-gray-500 underline">Back to sign in</a>
		</Link>
	)
}

// ---- Login ----------------------------------------------------------------

function LoginFormFields() {
	const form = useLoginForm() as unknown as AnyForm
	const showOtp = form.state === 'otp-required' || form.errors.some(e => e.field === 'otpToken')
	return (
		<div className="relative flex flex-col gap-4">
			{(form.state === 'submitting' || form.state === 'success') && <Loader />}
			<FormErrors form={form} />
			<Field form={form} field="email" label="E-mail" type="email" autoComplete="email" autoFocus />
			<Field form={form} field="password" label="Password" type="password" autoComplete="current-password" />
			{showOtp && <Field form={form} field="otpToken" label="Verification code (2FA)" type="text" autoComplete="one-time-code" />}
			<SubmitButton form={form}>Sign in</SubmitButton>
			<Link to="login/reset-request">
				<a className="text-center text-sm text-gray-500 underline">Forgot your password?</a>
			</Link>
		</div>
	)
}

export function LoginScreen() {
	return (
		<Shell>
			<LoginForm>
				<form className="grid gap-4">
					<LoginFormFields />
				</form>
			</LoginForm>
		</Shell>
	)
}

export function LoggedIn() {
	const identity = useIdentity()
	useEffect(() => {
		const t = setTimeout(() => {
			window.location.href = adminConfig.appUrl
		}, 400)
		return () => clearTimeout(t)
	}, [])
	return (
		<Shell subtitle="Signed in successfully">
			<p className="text-sm text-gray-700">
				Signed in as <strong>{identity?.person?.email ?? '…'}</strong>. Redirecting…
			</p>
			<Loader />
		</Shell>
	)
}

export function LoadingScreen() {
	return (
		<Shell>
			<Loader />
		</Shell>
	)
}

// ---- Password reset -------------------------------------------------------

function ResetRequestFields() {
	const form = usePasswordResetRequestForm() as unknown as AnyForm
	return (
		<div className="relative flex flex-col gap-4">
			{(form.state === 'submitting' || form.state === 'success') && <Loader />}
			<FormErrors form={form} />
			<Field form={form} field="email" label="E-mail" type="email" autoComplete="email" autoFocus />
			<SubmitButton form={form}>Send a reset link</SubmitButton>
		</div>
	)
}

export function ResetRequestScreen({ onSuccess }: { onSuccess: () => void }) {
	return (
		<Shell subtitle="Enter your email and we will send you a password reset code">
			<PasswordResetRequestForm onSuccess={onSuccess}>
				<form className="grid gap-4">
					<ResetRequestFields />
				</form>
			</PasswordResetRequestForm>
			<BackToLogin />
		</Shell>
	)
}

export function ResetRequestSentScreen() {
	return (
		<Shell subtitle="Check your email">
			<p className="text-sm text-gray-700">
				If an account with that address exists, we have sent it instructions and a code for setting a new password.
			</p>
			<Link to="login/password-reset">
				<a className="mt-4 block text-center text-sm text-blue-primary underline hover:text-orange">I have a code — set a new password</a>
			</Link>
			<BackToLogin />
		</Shell>
	)
}

function PasswordResetFields({ hasToken }: { hasToken: boolean }) {
	const form = usePasswordResetForm() as unknown as AnyForm
	const tokenError = form.errors.some(e => e.field === 'token')
	return (
		<div className="relative flex flex-col gap-4">
			{(form.state === 'submitting' || form.state === 'success') && <Loader />}
			<FormErrors form={form} />
			{(!hasToken || tokenError) && <Field form={form} field="token" label="Code from the email" type="text" autoComplete="off" autoFocus />}
			<Field form={form} field="password" label="New password" type="password" autoComplete="new-password" autoFocus={hasToken} />
			<Field
				form={form}
				field="passwordConfirmation"
				label="New password again"
				type="password"
				autoComplete="new-password"
			/>
			<SubmitButton form={form}>Set password</SubmitButton>
		</div>
	)
}

export function PasswordResetScreen({ token, onSuccess }: { token?: string; onSuccess: () => void }) {
	return (
		<Shell subtitle="Set a new password">
			<PasswordResetForm token={token} onSuccess={onSuccess}>
				<form className="grid gap-4">
					<PasswordResetFields hasToken={!!token} />
				</form>
			</PasswordResetForm>
			<BackToLogin />
		</Shell>
	)
}
